import { Dispatch, SetStateAction, useEffect, useState } from "react";

import {
  ArchiveBoxXMarkIcon,
  ExclamationTriangleIcon,
  PauseCircleIcon,
  StopCircleIcon,
} from "@heroicons/react/24/outline";

import Modal from "../../layouts/Modal";

interface ScanVehicleActionModalProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  /** Name of the assembly whose timer is currently running. */
  assemblyName?: string;
  /** How many parts in that assembly are still outstanding. */
  outstandingCount: number;
  onMarkAllRemoved: () => void;
  onMarkAllDamaged: () => void;
  onPause: (reason: string) => void;
  onFinish: () => void;
}

// Shown when the job's vehicle is scanned while an assembly timer is running and
// parts are still outstanding. The user can clear the remaining parts in bulk,
// pause (with a reason) or finish the timer. Scanning the vehicle again while
// this is open finishes the timer (handled by the caller).
export default function ScanVehicleActionModal({
  open,
  setOpen,
  assemblyName,
  outstandingCount,
  onMarkAllRemoved,
  onMarkAllDamaged,
  onPause,
  onFinish,
}: ScanVehicleActionModalProps) {
  const [pausing, setPausing] = useState(false);
  const [reason, setReason] = useState("");

  // Always reopen on the menu view with a clean reason.
  useEffect(() => {
    if (!open) {
      setPausing(false);
      setReason("");
    }
  }, [open]);

  const actionButton =
    "inline-flex flex-1 items-center justify-center gap-x-2 rounded-md border border-transparent shadow-sm px-4 py-3 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-etpink-500";

  return (
    <Modal open={open} setOpen={setOpen} width={"max-w-lg"}>
      <div className="text-center">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          {assemblyName ?? "Assembly"} timer running
        </h3>
        <p className="text-sm text-gray-600 mt-2">
          {outstandingCount} part{outstandingCount === 1 ? "" : "s"} still need removing or marking as damaged.
        </p>
        <p className="text-xs text-gray-400 mt-1">
          Scan the vehicle again to finish the timer.
        </p>
      </div>

      {pausing ? (
        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700">Pause reason</label>
          <textarea
            className="mt-1 w-full rounded-md border border-gray-300 p-2 text-sm focus:border-primary-500 focus:ring-primary-500"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why is the timer being paused?"
            autoFocus
          />
          <div className="mt-4 flex gap-3">
            <button
              type="button"
              disabled={!reason.trim()}
              className={`${actionButton} bg-primary-600 hover:bg-primary-700 disabled:opacity-50`}
              onClick={() => onPause(reason.trim())}
            >
              Confirm Pause
            </button>
            <button
              type="button"
              className="inline-flex flex-1 items-center justify-center rounded-md border border-gray-300 px-4 py-3 bg-white text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-etpink-500"
              onClick={() => setPausing(false)}
            >
              Back
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <button type="button" className={`${actionButton} bg-primary-600 hover:bg-primary-700`} onClick={onMarkAllRemoved}>
              <ArchiveBoxXMarkIcon className="h-5 w-5" aria-hidden="true" />
              Remove Remaining
            </button>
            <button type="button" className={`${actionButton} bg-red-600 hover:bg-red-700`} onClick={onMarkAllDamaged}>
              <ExclamationTriangleIcon className="h-5 w-5" aria-hidden="true" />
              Mark Remaining as Damaged
            </button>
            <button type="button" className={`${actionButton} bg-amber-500 hover:bg-amber-600`} onClick={() => setPausing(true)}>
              <PauseCircleIcon className="h-5 w-5" aria-hidden="true" />
              Pause Timer
            </button>
            <button type="button" className={`${actionButton} bg-green-600 hover:bg-green-700`} onClick={onFinish}>
              <StopCircleIcon className="h-5 w-5" aria-hidden="true" />
              Finish Timer
            </button>
          </div>

          <button
            type="button"
            className="mt-4 w-full inline-flex justify-center rounded-md border border-gray-300 px-4 py-2 bg-white text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-etpink-500 sm:text-sm"
            onClick={() => setOpen(false)}
          >
            Cancel
          </button>
        </>
      )}
    </Modal>
  );
}
